import { useState, useMemo } from "react";
import {
  useGetBlogPostsQuery,
  useDeleteBlogPostMutation,
} from "../../features/api/blogApiSlice";
import FuelLoading from "../../pages/FuelLoading";
import "./blogs.scss";
import { useToast } from "../toast/ToastContext";
import AdminHeader from "../layout/AdminHeader";
import ConfirmModal from "../modal/ConfirmModal";

export default function AdminBlogDashboard() {
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchVal, setSearchVal] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const { showToast } = useToast();

  // 🟢 NEW: MODAL INTERACTIVE STATE TRACKERS
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    targetId: null,
    targetTitle: "",
  });

  // Fire queries utilizing your updated slice fields parameters
  const { data, isLoading, isError, isFetching } = useGetBlogPostsQuery({
    page,
    limit,
    adminMode: true,
    search: searchVal,
    category: categoryFilter,
  });

  const [deleteBlogPost, { isLoading: isDeleting }] =
    useDeleteBlogPostMutation();

  const posts = data?.items || [];
  const totalPages = data?.totalPages || 1;
  const totalPostsCount = data?.total || 0;

  // Derive compact metrics from total data items loaded under current query conditions
  const publishedCount = posts.filter((p) => p.isPublished).length;
  const draftCount = posts.filter((p) => !p.isPublished).length;

  // Filter out matching datasets based on internal tab status state ('live' vs 'draft')
  const filteredPosts = useMemo(() => {
    if (statusFilter === "live") return posts.filter((p) => p.isPublished);
    if (statusFilter === "draft") return posts.filter((p) => !p.isPublished);
    return posts;
  }, [posts, statusFilter]);

  // Open custom modal instead of old native browser prompt window
  const handleDeleteTrigger = (id, title) => {
    setModalConfig({ isOpen: true, targetId: id, targetTitle: title });
  };

  const executeDeletePurge = async () => {
    const { targetId, targetTitle } = modalConfig;
    setModalConfig({ isOpen: false, targetId: null, targetTitle: "" });

    try {
      await deleteBlogPost(targetId).unwrap();
      showToast(
        `"${targetTitle}" successfully purged from journal archives.`,
        "success"
      );
    } catch (err) {
      const errorMsg =
        err.data?.message || err.error || "Server validation error.";
      showToast(`Purge operation aborted: ${errorMsg}`, "error");
    }
  };

  if (isLoading) {
    return (
      <div className="admin-blog-loading-box">
        <FuelLoading text="Opening journal file vaults..." />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="admin-blog-error">
        ⚠️ Secure data access denied or database is offline.
      </div>
    );
  }

  return (
    <div className="admin-blog-dashboard fuel-fade-in">
      {/* 🟢 REUSABLE DYNAMIC CONFIRMATION MODAL */}
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        variant="warning"
        title="Purge Journal Post"
        confirmText="Purge Post"
        message={`Are you absolutely sure you want to permanently delete "${modalConfig.targetTitle}" from public indexes? This action cannot be reversed.`}
        onConfirm={executeDeletePurge}
        onCancel={() =>
          setModalConfig({ isOpen: false, targetId: null, targetTitle: "" })
        }
      />

      {/* 🟢 CUSTOM REUSABLE CENTRAL HEADER INJECTED */}
      <AdminHeader
        title="Journal Post Architecture Hub"
        description="Write new stories, tweak existing road trip publications, track drafting metrics, or organize travel catalog categories."
        badgeText="Articles Manager"
      />
      {/* TOP COMPACT SUMMARY CARDS DECK */}
      <header className="admin-blog-stats">
        <div className="admin-stat-chip">
          <strong>{totalPostsCount}</strong>
          <span>Total Matches</span>
        </div>
        <div className="admin-stat-chip admin-stat-chip--published">
          <strong>{publishedCount}</strong>
          <span>Live in Batch</span>
        </div>
        <div className="admin-stat-chip admin-stat-chip--draft">
          <strong>{draftCount}</strong>
          <span>Drafts in Batch</span>
        </div>
        <div className="admin-stat-chip admin-stat-chip--action">
          <a
            href="/admin/blog/create"
            className="fuel-primary-btn add-new-log-btn"
          >
            ✍️ Write New Post
          </a>
        </div>
      </header>

      {/* SEARCH AND FILTERS SEARCHBAR CHASSIS TOOLBAR */}
      <div className="table-filter-toolbar">
        <div className="filter-button-group">
          <button
            type="button"
            className={`filter-tab ${statusFilter === "all" ? "active" : ""}`}
            onClick={() => setStatusFilter("all")}
          >
            📁 All Logs
          </button>
          <button
            type="button"
            className={`filter-tab ${statusFilter === "live" ? "active" : ""}`}
            onClick={() => setStatusFilter("live")}
          >
            🟢 Live ({publishedCount})
          </button>
          <button
            type="button"
            className={`filter-tab ${statusFilter === "draft" ? "active" : ""}`}
            onClick={() => setStatusFilter("draft")}
          >
            📁 Drafts ({draftCount})
          </button>
        </div>

        <div className="search-filters-controls">
          <input
            type="text"
            className="search-input-field"
            placeholder="Search article title or slug..."
            value={searchVal}
            onChange={(e) => {
              setSearchVal(e.target.value);
              setPage(1); // Reset back to first batch on search typing inputs
            }}
          />

          <select
            className="category-dropdown-select"
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value);
              setPage(1); // Reset back to first batch on switch filter rules
            }}
          >
            <option value="">📁 All Categories</option>
            <option value="Expeditions">🗺️ Expeditions</option>
            <option value="Gear Review">⚙️ Gear Review</option>
            <option value="Road Notes">📝 Road Notes</option>
            <option value="Small Car Life">🚗 Small Car Life</option>
          </select>
        </div>
      </div>

      {/* JOURNAL MANAGEMENT DATA SHEET TABLE */}
      <section
        className={`admin-blog-table-zone ${
          isFetching ? "table-zone--fetching" : ""
        }`}
      >
        <div className="table-header-title">
          <h2>Article Library Logs</h2>
          {isFetching && (
            <span className="fetching-small-spinner">
              Syncing DB changes...
            </span>
          )}
        </div>

        <div className="admin-table-scroll-wrapper">
          <table className="admin-blog-table">
            <thead>
              <tr>
                <th>Cover Image</th>
                <th>Article Title</th>
                <th>Category</th>
                <th>Created Date</th>
                <th>Status State</th>
                <th style={{ textAlign: "right" }}>Actions Management</th>
              </tr>
            </thead>
            <tbody>
              {filteredPosts.length === 0 ? (
                <tr>
                  <td colSpan="6" className="table-empty-row">
                    No matching journal logs found on this route map track.
                  </td>
                </tr>
              ) : (
                filteredPosts.map((post) => (
                  <tr key={post._id}>
                    <td>
                      <img
                        src={post.coverImage}
                        alt=""
                        className="table-thumb"
                      />
                    </td>
                    <td className="table-title-cell">
                      <strong>{post.title}</strong>
                      <span>/{post.slug}</span>
                    </td>
                    <td>
                      <span className="table-category-tag">
                        {post.category}
                      </span>
                    </td>
                    <td>{new Date(post.createdAt).toLocaleDateString()}</td>
                    <td>
                      <span
                        className={`status-pill ${
                          post.isPublished
                            ? "status-pill--live"
                            : "status-pill--draft"
                        }`}
                      >
                        {post.isPublished ? "🟢 Live" : "📁 Draft"}
                      </span>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <div className="table-actions-cluster">
                        <a
                          href={`/admin/blog/edit/${post._id}`}
                          className="table-btn edit-btn"
                        >
                          ✏️ Edit
                        </a>
                        <button
                          type="button"
                          className="table-btn delete-btn"
                          disabled={isDeleting}
                          onClick={() =>
                            handleDeleteTrigger(post._id, post.title)
                          }
                        >
                          🗑️ Purge
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* COMPACT DASHBOARD TABLE PAGINATION CHASSIS */}
        {totalPages > 1 && (
          <nav className="table-pagination-nav">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(p - 1, 1))}
            >
              ◀ Prev Batch
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            >
              Next Batch ▶
            </button>
          </nav>
        )}
      </section>
    </div>
  );
}
